import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import EmojiFoodBeverageIcon from "@mui/icons-material/EmojiFoodBeverage";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FavoriteIcon from "@mui/icons-material/Favorite";

const values = [
  {
    icon: <EmojiFoodBeverageIcon sx={{ fontSize: 40, color: "#FCA500" }} />,
    title: "Authentic Flavors",
    desc: "Recipes that stay true to North Indian roots, every single day.",
  },
  {
    icon: <FavoriteIcon sx={{ fontSize: 40, color: "#FCA500" }} />,
    title: "Made with Love",
    desc: "From the kitchen, every bite is handcrafted with care.",
  },
  {
    icon: <LocalDiningIcon sx={{ fontSize: 40, color: "#FCA500" }} />,
    title: "Fresh Ingredients",
    desc: "Our commitment to freshness is what defines our food.",
  }
];

const timeline = [
  { year: "Feb 2024", event: "Started as our first outlet in City Light,Surat" },
  { year: "Jan 2025", event: "Opened second outlet in Piplod, Surat" },
  { year: "May 2025", event: "Launched our catering service" },
  { year: "July 2025", event: "Launched Breakfast(Tandoori Paratha) at City Light outlet" },
];

const testimonials = [
  {
    name: "Rajeev S.",
    quote: "Absolutely delicious! Kulche Shulche takes me back to Delhi streets.",
  },
  {
    name: "Megha P.",
    quote: "Clean, quick, and packed with flavor. My favorite weekend spot!",
  },
  {
    name: "Amit R.",
    quote: "Best chole kulche in Surat. Hands down.",
  },
];

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ bgcolor: "#FFF8E1", py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" fontWeight="bold" gutterBottom color="primary">
            About Kulche Shulche
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center">
            Ab{" "}
            <Box component="span" sx={{ color: "orange", fontWeight: "bold" }}>
              PUNJAB in Surat!
            </Box>{" "}
            We bring the authentic taste of North Indian street food to your plate, with a commitment to quality and flavor that keeps our customers coming back for more.
          </Typography>
        </Box>

        {/* Mission & Story */}
        <Grid
          container
          spacing={6}
          direction={isMobile ? "column-reverse" : "row"}
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Our Mission
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              At Kulche Shulche, our mission is rooted in passion and purpose. Coming from an engineering background but driven by a deep love for food and service, our founder envisioned a place where people could enjoy authentic Punjabi flavors prepared with integrity. With a commitment to zero artificial colors, premium-quality ingredients, top hygiene standards, and a transparent live kitchen, we strive to offer food that not only satisfies taste buds but also builds trust. Being a true foodie at heart, there's nothing more fulfilling than seeing the joy on our customers' faces after a satisfying meal — and that's the experience we aim to deliver every single day.
            </Typography>

            <Divider sx={{ my: 3, borderColor: "#FCA500" }} />

            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Our Story
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Kulche Shulche began its flavorful journey in February 2024 with the launch of our very first outlet in Surat. What started as a single step driven by passion for authentic Punjabi food and genuine hospitality quickly turned into a growing movement. Fueled by overwhelming love and support from our customers, we proudly opened our second outlet. Every milestone we’ve crossed reflects our dedication to quality, consistency, and the joy of serving good food that brings people together. Our story is just getting started — and you're a part of it.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
              }}
            >
              <Box
                component="img"
                src="/opening2.png"
                alt="About Kulche Shulche"
                sx={{
                  width: "100%",
                  maxWidth: 500, // optional: limit width on larger screens
                  borderRadius: 4,
                  boxShadow: 3,
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Timeline */}
        <Box mt={10}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" color="primary" gutterBottom>
            Our Journey
          </Typography>

          <Box mt={4}>
            {timeline.map((item, index) => (
              <Box
                key={index}
                sx={{
                  borderLeft: "4px solid #FCA500",
                  pl: 3,
                  py: 2,
                  mb: 2,
                  position: "relative",
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {item.year}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {item.event}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Values */}
        <Box mt={10}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" color="primary" gutterBottom>
            Why People Love Us
          </Typography>

          <Grid container spacing={4} mt={2}>
            {values.map((val, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box textAlign="center" px={2}>
                  {val.icon}
                  <Typography variant="h6" fontWeight="bold" mt={2}>
                    {val.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {val.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>



        {/* Testimonials */}
        {/* <Box mt={10}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" color="primary" gutterBottom>
            What Our Customers Say
          </Typography>

          <Grid container spacing={4} mt={2}>
            {testimonials.map((review, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: "#ffffff",
                    boxShadow: 2,
                    height: "100%",
                  }}
                >
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    “{review.quote}”
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    — {review.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box> */}
      </Container>
    </Box>
  );
};

export default About;
